import { getGlobalAccountsBaseURL, getGlobalPlaygroundAPIBaseURL } from './urls';
import { isCDNBuild } from './utils/build';
import { getRandomString } from './utils/random';
import { sha256 } from './utils/sha';

const ANIMA_TOKEN_LOCAL_STORAGE_KEY = 'anima:access_token';
const ANIMA_PLAYGROUND_TOKEN_LOCAL_STORAGE_KEY = 'anima:playground_app_access_token';

export interface AuthTheme {
  appName: string;
  appTitle?: string;
  appSubtitle?: string;
  primaryColor?: string;
  avatarUrl?: string;
}

export type LoginParams = AuthTheme;

export type AnimaAuthParams = {
  authToken?: string;
};

export class AnimaAuth {
  private isInitialized: boolean = false;

  constructor(private readonly params?: AnimaAuthParams) {
    if (params?.authToken) {
      // Auth token passed directly, no need to handle the auth flow
      this.isInitialized = true;
    } else if (this.$receivedRedirectCallback()) {
      // Received a redirect callback, we need to handle it to populate the auth token
      this.$handleRedirectCallback()
        .catch((error) => {
          console.error('Error handling redirect callback:', error);
        })
        .finally(() => {
          this.isInitialized = true;
        });
    } else {
      // No redirect callback, so we are initialized immediately
      this.isInitialized = true;
    }
  }

  async login(params?: LoginParams): Promise<void> {
    if (!isCDNBuild()) {
      throw new Error('AnimaAuth.login can only be called in a CDN browser environment');
    }

    const playgroundId = window.__ANIMA_PLAYGROUND_ID__;
    if (!playgroundId || playgroundId === 'undefined') {
      throw new Error('Playground ID is not set in window.__ANIMA_PLAYGROUND_ID__');
    }

    // Use origin + pathname + search to preserve query params in redirect URL
    const redirectUrl = window.location.origin + window.location.pathname + window.location.search;

    const { codeVerifier, codeChallenge } = await this.$generateCodeVerifierAndChallenge();
    const codeState = await this.$generateCodeState();

    localStorage.setItem('playground_code_verifier', codeVerifier);
    localStorage.setItem('playground_code_state', codeState);

    // Build state object with all parameters
    const stateData: Record<string, string> = {
      playground_scope_id: playgroundId,
      playground_code_challenge: codeChallenge,
      playground_code_state: codeState,
      playground_redirect_url: redirectUrl,
    };

    // Add theme fields if provided
    if (params) {
      stateData.playground_theme_app_name = params.appName;
      if (params.appTitle) stateData.playground_theme_app_title = params.appTitle;
      if (params.appSubtitle) stateData.playground_theme_app_subtitle = params.appSubtitle;
      if (params.primaryColor) stateData.playground_theme_primary_color = params.primaryColor;
      const avatarUrl = params.avatarUrl;
      if (avatarUrl) stateData.playground_theme_avatar_url = avatarUrl;
    }

    // Encode all parameters into a single base64 state parameter
    const stateJson = JSON.stringify(stateData);
    const stateBase64 = btoa(stateJson);

    const loginUrl = `${getGlobalAccountsBaseURL()}/login?state=${encodeURIComponent(stateBase64)}`;
    window.location.href = loginUrl;
  }

  async logout(): Promise<void> {
    if (!isCDNBuild()) {
      throw new Error('AnimaAuth.logout can only be called in a CDN browser environment');
    }

    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(ANIMA_TOKEN_LOCAL_STORAGE_KEY);
      localStorage.removeItem(ANIMA_PLAYGROUND_TOKEN_LOCAL_STORAGE_KEY);
    }
  }

  async $getToken(): Promise<string | null> {
    while (!this.isInitialized) {
      // Wait until the initialization is completed
      await new Promise((resolve) => setTimeout(resolve, 50));
    }

    if (this.params?.authToken) {
      return this.params.authToken;
    }
    if (typeof window !== 'undefined' && window.__ANIMA_TOKEN__) {
      return window.__ANIMA_TOKEN__;
    }
    if (typeof window !== 'undefined' && window.__ANIMA_PLAYGROUND_TOKEN__) {
      return window.__ANIMA_PLAYGROUND_TOKEN__;
    }
    if (typeof localStorage !== 'undefined') {
      const localStorageAnimaToken = localStorage.getItem(ANIMA_TOKEN_LOCAL_STORAGE_KEY);
      if (localStorageAnimaToken) {
        return localStorageAnimaToken;
      }
      const localStorageAnimaPlaygroundToken = localStorage.getItem(ANIMA_PLAYGROUND_TOKEN_LOCAL_STORAGE_KEY);
      if (localStorageAnimaPlaygroundToken) {
        return localStorageAnimaPlaygroundToken;
      }
    }

    return null;
  }

  async $generateCodeVerifierAndChallenge(): Promise<{
    codeVerifier: string;
    codeChallenge: string;
  }> {
    const codeVerifier = getRandomString(32);
    const codeChallenge = await sha256(codeVerifier);

    return { codeVerifier, codeChallenge };
  }

  async $generateCodeState(): Promise<string> {
    const codeState = getRandomString(32);
    return codeState;
  }

  $receivedRedirectCallback(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }
    const locationHash = window.location.hash;
    if (!locationHash) {
      return false;
    }
    return locationHash.includes('playground_request_token');
  }

  async $handleRedirectCallback(): Promise<void> {
    if (!this.$receivedRedirectCallback()) {
      return;
    }

    if (typeof window === 'undefined') {
      throw new Error('$handleRedirectCallback can only be called in a browser environment');
    }

    const locationHash = window.location.hash;

    // Reset the URL hash and remove the state query param to clean up the URL
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.delete('state');
    const cleanSearch = searchParams.toString() ? '?' + searchParams.toString() : '';
    history.replaceState(null, '', window.location.pathname + cleanSearch);

    const hashParams = new URLSearchParams(locationHash.slice(1));
    const playgroundRequestToken = hashParams.get('playground_request_token');
    const playgroundCodeState = hashParams.get('playground_code_state');

    if (!playgroundRequestToken) {
      console.error('playground_request_token is missing in the URL hash');
      return;
    }
    if (!playgroundCodeState) {
      console.error('playground_code_state is missing in the URL hash');
      return;
    }

    const storedCodeState = localStorage.getItem('playground_code_state');
    if (playgroundCodeState !== storedCodeState) {
      console.error('playground_code_state does not match the stored state, ignoring the callback');
      return;
    }

    const storedCodeVerifier = localStorage.getItem('playground_code_verifier');
    if (!storedCodeVerifier) {
      console.error('Stored code verifier is missing, cannot proceed with the authentication flow');
      return;
    }

    // If all checks pass, we can proceed with the authentication flow
    const token = await this.$requestToken(playgroundRequestToken, storedCodeVerifier);

    localStorage.setItem(ANIMA_PLAYGROUND_TOKEN_LOCAL_STORAGE_KEY, token);
    localStorage.removeItem('playground_code_verifier');
    localStorage.removeItem('playground_code_state');

    console.log('Successfully obtained token, user is logged in.');
  }

  async $requestToken(playgroundRequestToken: string, codeVerifier: string): Promise<string> {
    if (typeof window === 'undefined') {
      throw new Error('$requestToken can only be called in a browser environment');
    }

    const playgroundId = window.__ANIMA_PLAYGROUND_ID__;
    if (!playgroundId || playgroundId === 'undefined') {
      throw new Error('Playground ID is not set in window.__ANIMA_PLAYGROUND_ID__');
    }

    const url = `${getGlobalPlaygroundAPIBaseURL()}/${playgroundId}/auth/generate_session_token`;

    const response = await fetch(url, {
      method: 'POST',
      body: JSON.stringify({
        playground_request_token: playgroundRequestToken,
        playground_code_verifier: codeVerifier,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to exchange request token for session token: ${response.statusText}`);
    }

    const data = await response.json();
    return data.playground_app_access_token;
  }
}
